import { Link, NavLink } from 'react-router-dom';
import { Sprout, ShoppingBasket, Menu, X, MapPin, Truck, Leaf } from 'lucide-react';
import { useState } from 'react';
import { CartProvider, useCart } from './CartContext';
import './store.css';

function StoreHeader() {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const links = [
    { to: '/', label: 'Our Turf', end: true },
    { to: '/shop/order', label: 'Order' },
    { to: '/shop/track', label: 'Track Order' },
  ];

  return (
    <header className="sticky top-0 z-40" style={{ background: 'var(--s-green)' }} data-testid="store-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 text-white" data-testid="store-logo">
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--s-terra)' }}>
            <Sprout className="h-5 w-5" />
          </span>
          <span className="leading-tight">
            <span className="block font-heading font-bold text-base">Greenhill Creek Turf</span>
            <span className="block text-[10px] uppercase tracking-[0.2em] text-white/60">Queensland grown</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `store-nav-link ${isActive ? 'active' : ''}`} data-testid={`store-nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/shop/order" className="relative inline-flex items-center gap-2 text-white text-sm font-semibold rounded-full px-4 py-2 transition-colors" style={{ background: 'rgba(255,255,255,0.12)' }} data-testid="store-cart-btn">
            <ShoppingBasket className="h-4 w-4" />
            <span className="hidden sm:inline">My Order</span>
            {count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center" style={{ background: 'var(--s-terra)' }} data-testid="store-cart-count">{count}</span>
            )}
          </Link>
          <button className="md:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="store-mobile-menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="md:hidden border-t border-white/10 px-5 py-3 flex flex-col gap-1" style={{ background: 'var(--s-green)' }}>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} className={({ isActive }) => `store-nav-link ${isActive ? 'active' : ''}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}

function StoreFooter() {
  return (
    <footer className="mt-auto text-white" style={{ background: 'var(--s-ink)' }} data-testid="store-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid gap-10 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Sprout className="h-6 w-6" style={{ color: 'var(--s-green-3)' }} />
            <span className="font-heading font-bold text-lg">Greenhill Creek Turf</span>
          </div>
          <p className="text-sm text-white/60 leading-relaxed max-w-sm">
            A family-run turf farm growing premium lawn varieties in Queensland. Every roll is cut fresh to order and on your doorstep within hours.
          </p>
        </div>
        <div className="space-y-3 text-sm text-white/70">
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4" style={{ color: 'var(--s-terra)' }} /> Greenhill Creek, Queensland, Australia</p>
          <p className="flex items-center gap-2"><Truck className="h-4 w-4" style={{ color: 'var(--s-terra)' }} /> Delivery across South East Queensland</p>
          <p className="flex items-center gap-2"><Leaf className="h-4 w-4" style={{ color: 'var(--s-terra)' }} /> Farm pickup available by arrangement</p>
        </div>
        <div className="text-sm text-white/60 md:text-right">
          <p className="font-heading font-semibold text-white mb-3">Quick links</p>
          <div className="flex md:justify-end gap-4 flex-wrap">
            <Link to="/" className="hover:text-white">Our Turf</Link>
            <Link to="/shop/order" className="hover:text-white">Order</Link>
            <Link to="/shop/track" className="hover:text-white">Track Order</Link>
            <Link to="/login" className="hover:text-white" data-testid="store-staff-login-link">Staff login</Link>
          </div>
          <p className="mt-8 text-xs text-white/40">© {new Date().getFullYear()} Greenhill Creek Turf</p>
        </div>
      </div>
    </footer>
  );
}

export function StoreLayout({ children }) {
  return (
    <CartProvider>
      <div className="store" data-testid="store-layout">
        <StoreHeader />
        <main className="flex-1">{children}</main>
        <StoreFooter />
      </div>
    </CartProvider>
  );
}
